<template>
  <sc-layout
    :has-aside="
      Boolean(isAdvisor && hasNewTemplate && !isEmbeddedInNext && !isInIframe)
    "
    :disable-padding="$route.meta.layoutPaddingDisabled"
    :disable-navigation="
      $route.meta.topNavigationDisabled ||
        $route.meta.embeddable ||
        (isEmbeddedInNext && isInIframe)
    "
  >
    <!-- Navigation: Left -->
    <div slot="nav-left">
      <sc-flex
        v-if="$route.meta.requiresAuth && !$route.meta.embeddable"
        @click="onHomeClick"
        class="navigation-left-logo"
      >
        <img
          v-if="firmLogo"
          :src="firmLogo"
          :class="$vuetify.breakpoint.smAndUp ? 'logo' : 'mobile-logo'"
        />
        <sc-soraban v-else :size="16"></sc-soraban>
      </sc-flex>

      <sc-flex
        v-if="
          !$route.meta.requiresAuth &&
            !$route.meta.embeddable &&
            !notificationLoading
        "
      >
        <sc-button-group>
          <sc-button
            type="secondary"
            icon-left="arrow-left"
            @click="$router.push('/')"
          >
            Back
          </sc-button>
        </sc-button-group>
      </sc-flex>
    </div>

    <!-- Slot: Middle -->
    <div
      slot="nav-middle"
      v-if="false"
      :class="
        $route.path !== '/overview' && 'tmp-client-view-responsive-banner'
      "
    >
      <sc-layout-banner-bar icon="star" @click="onTaxBannerClick">
        We are very excited to release three new powerful features powered by AI
        to save more admin time for your team. Click <b>here</b> to learn more.
        <sc-button-group slot="button-group">
          <sc-button type="ghost" @click="dismissTaxBanner" size="small"
            >Dismiss</sc-button
          >
        </sc-button-group>
      </sc-layout-banner-bar>
    </div>
    <div
      slot="nav-middle"
      v-if="hasExportBanner && !exportBannerDismissed && isAdvisor"
      :class="
        $route.path !== '/overview' && 'tmp-client-view-responsive-banner'
      "
    >
      <sc-layout-banner-bar icon="star">
        We’ve updated the design of our questionnaire PDF export and enhanced
        the bookmarking feature! We’d love to hear your feedback through the
        chat bubble on bottom right
        <sc-button-group slot="button-group">
          <sc-button type="ghost" @click="dismissExportBanner" size="small"
            >Dismiss</sc-button
          >
        </sc-button-group>
      </sc-layout-banner-bar>
    </div>

    <!-- Navigation: Right -->
    <div slot="nav-right">
      <sc-flex
        :is-y-centered="true"
        :gap="8"
        v-if="$route.meta.requiresAuth && !$route.meta.embeddable"
      >
        <!-- Alert: Pending refresh(?) -->
        <RefreshUiAlert @showAlert="pendingRefresh = true" />

        <!-- Alert: Globals -->
        <GlobalUiAlert
          @change="globalAlertVisible = $event"
          v-if="isAdvisor && $route.meta.sideNavigationOff"
        />

        <!-- Header: Advisor -->
        <v-flex justify-center v-if="isAdvisor && isClientView">
          <v-layout
            align-center
            style="max-width: 400px"
            class="hidden-xs-only"
          >
            <div class="mr-2 darkgrey--text">Selected Entity</div>
            <v-autocomplete
              class="default-input ph-no-capture"
              :items="companiesWithAdminView"
              :value="currentCompanyId"
              solo
              light
              hide-details
              :item-text="isAdvisor ? 'adminView' : 'clientView'"
              item-value="id"
              @change="updateDefaultCompany"
            />
          </v-layout>
        </v-flex>

        <!-- Header: Multi-entity, not advisor -->
        <sc-flex
          :gap="12"
          :is-y-centered="true"
          v-else-if="multiEntity && !isAdvisor"
        >
          <sc-flex :is-column="true" class="current-entity__container">
            <sc-tiny class="hidden-xs-only" color="gray"
              >{{ $t('selectEntity.currentEntity') }}:</sc-tiny
            >
            <sc-p>
              <b class="default-company-name">
                {{ defaultCompany.tax_return_name }}
              </b>
            </sc-p>
          </sc-flex>
          <sc-button-group>
            <sc-button
              class="current-entity__container__switch--desktop"
              size="small"
              icon-left="switch"
              type="secondary"
              @click="showSelectEntityDialog"
            >
              {{ $t('selectEntity.switchEntity') }}
            </sc-button>
            <sc-button
              class="current-entity__container__switch--mobile"
              size="small"
              :is-icon-only="true"
              icon-left="switch"
              type="secondary"
              @click="showSelectEntityDialog"
            >
              {{ $t('selectEntity.switchEntity') }}
            </sc-button>
          </sc-button-group>
        </sc-flex>

        <!-- Tooltip: Not advisor -->
        <v-tooltip
          right
          :disabled="$vuetify.breakpoint.smAndDown"
          v-if="!isAdvisor"
        >
          <v-btn
            flat
            slot="activator"
            class="notifications-button primary--text mb-2 mr-0"
            icon
            @click="$router.push('/help')"
          >
            <i class="darkgrey--text material-icons-outlined ml-4"
              >help_outlined</i
            >
          </v-btn>
          <span>Help</span>
        </v-tooltip>

        <!-- Header: With new threads -->
        <sc-button-group v-if="hasNewChat">
          <sc-button
            @click="onNewChatClick"
            size="small"
            type="ghost"
            :is-rounded="true"
            icon-left="chat"
            :is-icon-only="true"
          ></sc-button>
        </sc-button-group>

        <!-- Header: With firm communications off -->
        <template v-if="!isFirmCommunicationDisabled && !hasNewChat">
          <!-- Menu -->
          <v-menu
            transition="slide-y-transition"
            bottom
            nudge-bottom="50"
            :class="$vuetify.breakpoint.mdAndUp ? 'ml-4' : 'mx-0'"
            v-if="isAdvisor || (myConversations && myConversations.length > 1)"
          >
            <v-tooltip
              left
              slot="activator"
              :disabled="$vuetify.breakpoint.smAndDown"
            >
              <v-btn
                slot="activator"
                class="conversations-button primary--text mx-0"
                flat
                icon
              >
                <v-badge
                  :color="colorOfConversationBadge"
                  style="cursor: pointer"
                  :value="true"
                  overlap
                >
                  <i class="darkgrey--text material-icons-outlined mt-1"
                    >chat_bubble_outline</i
                  >
                  <div slot="badge" v-if="!isEmpty(unseenConversations)">
                    {{ unseenConversations.length }}
                  </div>
                </v-badge>
              </v-btn>
              <span
                >Shortcut
                <div class="shortcut-key">m</div></span
              >
            </v-tooltip>
            <v-card style="max-height: calc(100vh - 64px); overflow: auto">
              <v-layout align-center class="py-2" style="height: 40px">
                <div class="text-xs-left subheading ml-3">
                  {{ $t('myConversations') }}
                </div>
                <v-spacer />
                <v-icon
                  v-if="isAdvisor"
                  style="cursor: pointer"
                  class="black--text mr-3"
                  @click="
                    selectConversationDialogOpened = true;
                    selectedConversationId = null;
                  "
                  >add</v-icon
                >
              </v-layout>
              <v-list
                class="scrollable"
                three-line
                :style="
                  $vuetify.breakpoint.mdAndUp
                    ? 'width: 450px;max-height: 650px;'
                    : 'max-width: 400px;max-height: 500px;'
                "
              >
                <v-list-tile
                  v-for="(conversation, i) in filteredConversations"
                  :key="i"
                  class="mx-2 mb-2 conversation-list-item"
                  :class="
                    conversation.unseen_messages.some(
                      m =>
                        m &&
                        m.unseen_user_ids &&
                        m.unseen_user_ids.includes(currentUser.id),
                    )
                      ? 'lighten-5 bordered-item'
                      : 'grey lighten-5'
                  "
                  @click="openConversationDialog(conversation.id)"
                >
                  <v-layout class="text-xs-left py-2" column>
                    <v-layout align-center class="pb-1">
                      <span
                        v-if="conversation.name"
                        class="text-truncate ph-no-capture"
                        >{{ conversation.name }}</span
                      >
                      <span class="text-truncate ph-no-capture" v-else>{{
                        getNamesInConversationExceptMe(conversation)
                      }}</span>
                      <v-chip
                        small
                        round
                        class="ml-2 my-0 error white--text"
                        v-if="
                          conversation.unseen_messages.some(
                            m =>
                              m &&
                              m.unseen_user_ids &&
                              m.unseen_user_ids.includes(currentUser.id) &&
                              m.tag === 'emergency',
                          )
                        "
                      >
                        Emergency
                      </v-chip>
                      <v-chip
                        small
                        round
                        class="ml-2 my-0 warning"
                        v-else-if="
                          conversation.unseen_messages.some(
                            m =>
                              m &&
                              m.unseen_user_ids &&
                              m.unseen_user_ids.includes(currentUser.id) &&
                              m.tag === 'urgent',
                          )
                        "
                      >
                        Urgent
                      </v-chip>
                      <v-chip
                        small
                        round
                        class="ml-2 my-0 primary white--text"
                        v-else-if="getNumberOfUnseenMessages(conversation) > 0"
                      >
                        {{ getNumberOfUnseenMessages(conversation) }} New
                      </v-chip>
                      <v-spacer />
                      <v-tooltip
                        bottom
                        v-if="
                          conversation.unseen_messages.some(
                            m =>
                              m &&
                              m.unseen_user_ids &&
                              m.unseen_user_ids.includes(currentUser.id),
                          )
                        "
                      >
                        <v-btn
                          slot="activator"
                          small
                          fab
                          style="height: 20px; width: 40px"
                          @click.native.stop
                          class="primary--text ml-2 my-0 pa-0"
                          flat
                          @click.prevent.stop="markAsRead(conversation)"
                        >
                          <v-icon size="18">visibility</v-icon>
                        </v-btn>
                        <span>Mark as seen</span>
                      </v-tooltip>
                    </v-layout>
                    <v-layout
                      align-center
                      style="cursor: pointer"
                      class="notification__text"
                      v-if="conversation.last_message"
                    >
                      <div
                        class="text-truncate caption"
                        v-if="
                          conversation &&
                            conversation.last_message &&
                            conversation.last_message.text.includes(
                              '$$attached$$',
                            )
                        "
                      >
                        <span
                          class="primary--text font-weight-bold"
                          v-if="
                            conversation.last_message.user_id === currentUser.id
                          "
                        >
                          You
                        </span>
                        <span class="primary--text font-weight-bold" v-else>{{
                          conversation.last_message.user_name
                        }}</span>
                        attached
                        {{ getDocumentName(conversation.last_message) }}
                      </div>
                      <div class="text-truncate caption" v-else>
                        <span
                          class="primary--text font-weight-bold ph-no-capture"
                          v-if="
                            conversation.last_message.user_id !== currentUser.id
                          "
                        >
                          {{ conversation.last_message.user_name }}:
                        </span>
                        <span class="primary--text font-weight-bold" v-else>
                          You:
                        </span>
                        <template
                          v-if="getNumberOfUnseenMessages(conversation) > 0"
                        >
                          <span
                            v-html="
                              getUnseenMessagesByCurrentUser(
                                conversation,
                              ).text.split('<br/>')[0] +
                                (getUnseenMessagesByCurrentUser(
                                  conversation,
                                ).text.split('<br/>').length > 1
                                  ? ' ...'
                                  : '')
                            "
                          />
                        </template>
                        <template v-else>
                          <span
                            v-html="
                              conversation.last_message.text.split('<br/>')[0] +
                                (conversation.last_message.text.split('<br/>')
                                  .length > 1
                                  ? ' ...'
                                  : '')
                            "
                          />
                        </template>
                      </div>
                      <v-spacer />
                      <div
                        class="caption grey--text text-xs-center notification__time ml-2"
                        style="min-width: 50px"
                      >
                        {{
                          moment(
                            getNumberOfUnseenMessages(conversation) > 0
                              ? getUnseenMessagesByCurrentUser(conversation)
                                  .created_at
                              : conversation.last_message.created_at,
                          ).fromNow()
                        }}
                      </div>
                    </v-layout>
                  </v-layout>
                </v-list-tile>
                <v-list-tile v-if="isEmpty(filteredConversations)">
                  <v-layout
                    align-center
                    class="grey--text body-1 text-xs-center"
                  >
                    <v-flex>
                      <v-icon class="mr-1" size="18">priority_high</v-icon>No
                      <span
                        v-if="
                          myConversations.length && !unseenConversations.length
                        "
                        >New </span
                      >Messages
                    </v-flex>
                  </v-layout>
                </v-list-tile>
              </v-list>
              <v-layout
                class="py-2"
                v-if="!isEmpty(myConversations)"
                justify-center
              >
                <v-btn
                  outline
                  small
                  @click="viewAllConversations = true"
                  @click.native.stop
                  v-if="!viewAllConversations"
                  class="my-0 notification__time caption text-capitalize"
                >
                  View All
                </v-btn>
                <v-btn
                  outline
                  small
                  @click="viewAllConversations = false"
                  @click.native.stop
                  v-else
                  class="my-0 notification__time caption text-capitalize"
                >
                  Hide Seen
                </v-btn>
              </v-layout>
            </v-card>
          </v-menu>

          <!-- Button -->
          <v-btn
            v-else
            class="conversations-button primary--text ml-2"
            flat
            icon
            @click="
              isEmpty(myConversations)
                ? (selectConversationDialogOpened = true)
                : openConversationDialog(myConversations[0].id)
            "
          >
            <v-badge
              color="primary"
              style="cursor: pointer"
              :value="true"
              overlap
            >
              <i class="darkgrey--text material-icons-outlined mt-1"
                >chat_bubble_outline</i
              >
              <div slot="badge" v-if="!isEmpty(unseenConversations)">
                {{ unseenConversations.length }}
              </div>
            </v-badge>
          </v-btn>
        </template>

        <!-- Menu: Advisor -->
        <v-menu
          transition="slide-y-transition"
          bottom
          nudge-bottom="50"
          v-if="isAdvisor"
        >
          <v-tooltip
            right
            slot="activator"
            :disabled="$vuetify.breakpoint.smAndDown"
          >
            <v-btn
              flat
              slot="activator"
              class="notifications-button primary--text ml-0"
              icon
            >
              <v-badge
                color="primary"
                style="cursor: pointer"
                :value="true"
                overlap
              >
                <i class="darkgrey--text material-icons-outlined mt-1"
                  >notifications</i
                >
                <div
                  slot="badge"
                  style="font-size: 12px"
                  v-if="!isEmpty(undismissedNotifications)"
                >
                  {{ undismissedNotifications.length }}
                </div>
              </v-badge>
            </v-btn>
            <span
              >Shortcut
              <div class="shortcut-key">n</div></span
            >
          </v-tooltip>
          <v-card>
            <v-layout
              class="text-xs-left subheading py-2 ml-3 dashboard__label"
              style="height: 40px"
            >
              My Notifications
              <v-spacer />
              <v-progress-circular
                class="mr-4"
                v-if="notificationLoading"
                indeterminate
                size="25"
                color="darkgrey"
              ></v-progress-circular>
              <v-tooltip bottom v-else>
                <v-icon
                  slot="activator"
                  class="mr-3"
                  @click.native.stop
                  @click="refreshNotifications()"
                  >refresh</v-icon
                >
                <span>Refresh</span>
              </v-tooltip>
            </v-layout>
            <v-list
              two-line
              :style="
                $vuetify.breakpoint.mdAndUp
                  ? 'width: 450px;max-height: 650px;'
                  : 'max-width: 400px;max-height: 500px;'
              "
              class="scrollable global-notification-container ph-no-capture"
              flat
            >
              <v-spacer />
              <v-list-tile
                v-for="(notification, i) in filteredNotifications"
                :key="i"
                class="caption mb-2 mx-2"
                :class="notificationColor(notification)"
                @click="handleNotification(notification, false)"
              >
                <v-layout class="text-xs-left" align-center>
                  <div>
                    <v-tooltip
                      bottom
                      v-if="
                        (notification.client_name &&
                          notification.client_name.length +
                            notification.text.length >=
                            100) ||
                          notification.text.length >= 100
                      "
                    >
                      <v-layout slot="activator">
                        <v-flex
                          class="notification__text mr-1"
                          v-if="notification.company_id > 0"
                        >
                          <span class="font-weight-bold">{{
                            notification.client_name
                          }}</span>
                          <span class="notification__time"
                            >{{ truncateString(notification.text, 90)
                            }}{{ forWording(notification) }}</span
                          >
                        </v-flex>
                        <v-flex
                          class="notification__text mr-1"
                          v-else-if="isReminder(notification)"
                        >
                        </v-flex>
                        <v-flex class="notification__text mr-1" v-else>
                          <span class="font-weight-bold">Soraban</span>
                          <span class="notification__time">{{
                            truncateString(notification.text, 90)
                          }}</span>
                        </v-flex>
                      </v-layout>
                      <v-layout style="max-width: 400px" wrap
                        >{{
                          notification.company_id > 0
                            ? notification.client_name
                            : 'Soraban'
                        }}
                        {{ notification.text
                        }}{{ forWording(notification) }}</v-layout
                      >
                    </v-tooltip>
                    <v-layout v-else>
                      <v-flex
                        class="notification__text mr-1"
                        v-if="notification.company_id > 0"
                      >
                        <span class="font-weight-bold">{{
                          notification.client_name
                        }}</span>
                        <span class="notification__time"
                          >{{ notification.text
                          }}{{ forWording(notification) }}</span
                        >
                      </v-flex>
                      <v-flex
                        class="notification__text mr-1"
                        v-else-if="isReminder(notification)"
                      >
                      </v-flex>
                      <v-flex class="notification__text mr-1" v-else>
                        <span class="font-weight-bold">Soraban</span>
                        <span class="notification__time">{{
                          notification.text
                        }}</span>
                      </v-flex>
                    </v-layout>
                    <div
                      class="grey--text caption text-xs-left notification__time"
                    >
                      {{ moment(notification.created_at).fromNow() }}
                    </div>
                  </div>
                  <v-spacer />
                  <v-btn
                    small
                    fab
                    @click.native.stop
                    v-if="!notification.dismissed"
                    class="error--text ma-0 pa-0"
                    flat
                    @click="dismissNotification(notification)"
                  >
                    <v-icon size="18">clear</v-icon>
                  </v-btn>
                </v-layout>
              </v-list-tile>
              <v-list-tile v-if="isEmpty(filteredNotifications)">
                <v-layout column class="grey--text body-1 text-xs-center"
                  >No Notifications</v-layout
                >
              </v-list-tile>
            </v-list>
            <v-layout justify-center class="py-2">
              <v-btn
                outline
                small
                @click="viewAllNotifications = true"
                @click.native.stop
                v-if="!viewAllNotifications"
                class="my-0 notification__time caption text-capitalize"
              >
                View All
              </v-btn>
              <v-btn
                outline
                small
                @click="viewAllNotifications = false"
                @click.native.stop
                v-else
                class="my-0 notification__time caption text-capitalize"
              >
                View Undismissed
              </v-btn>
              <v-btn
                outline
                small
                @click="dismissAllNotifications()"
                v-if="undismissedNotifications.length > 0"
                @click.native.stop
                class="my-0 notification__time caption text-capitalize"
              >
                Dismiss All ({{ undismissedNotifications.length }})
              </v-btn>
              <v-btn
                outline
                small
                @click="downloadAllNotifications()"
                @click.native.stop
                v-if="undismissedDownloadableNotifications.length > 1"
                class="my-0 notification__time caption text-capitalize"
              >
                Download All ({{ undismissedDownloadableNotifications.length }})
              </v-btn>
            </v-layout>
          </v-card>
        </v-menu>

        <!-- Menu: Generic -->
        <v-menu
          transition="slide-y-transition"
          bottom
          nudge-bottom="50"
          class="pr-0 mr-0 sb-main-menu"
        >
          <v-btn
            slot="activator"
            flat
            id="account-menu"
            class="default-input text-capitalize pr-0"
            style="height: 50px"
          >
            <v-avatar class="primary mr-2" size="40" v-if="isAdvisor">
              <img
                v-if="currentUser.thumb_url"
                :src="currentUser.thumb_url"
                alt="Profile pic"
              />
              <v-icon v-else color="white"> person </v-icon>
            </v-avatar>
            <v-layout
              column
              align-center
              :class="$vuetify.breakpoint.mdAndUp ? 'mr-3' : 'mr-0'"
            >
              <v-flex
                class="subheading ph-no-capture"
                v-if="$vuetify.breakpoint.mdAndUp"
              >
                {{ currentUser.full_name }}
              </v-flex>
              <v-flex class="subheading" v-else>
                {{ userInitials }}
              </v-flex>
              <v-flex class="grey--text" v-if="isSuperAdvisor">
                Administrator
              </v-flex>
              <v-flex
                class="grey--text"
                v-else-if="isAdvisor && currentUser.firm_access === 'partner'"
              >
                Admin
              </v-flex>
              <v-flex
                class="grey--text"
                v-else-if="isAdvisor && currentUser.firm_access !== 'partner'"
              >
                Staff
              </v-flex>
            </v-layout>
            <v-icon>keyboard_arrow_down</v-icon>
          </v-btn>
          <v-list>
            <v-list-tile
              v-if="
                $router.currentRoute.meta.newDashboard !== true || isAdvisor
              "
              v-for="(item, i) in accountMenu"
              :key="i"
              @click="goToPath(item.path)"
              :id="item.id"
            >
              <v-tooltip
                right
                v-if="item.shortcut"
                :disabled="$vuetify.breakpoint.smAndDown"
              >
                <v-list-tile-title slot="activator">
                  <v-layout align-center>
                    <i class="material-icons-outlined menu-icon mr-2">{{
                      item.icon
                    }}</i>
                    <span class="menu-text">{{
                      item.name ? $t('accountMenu.' + item.name) : item.title
                    }}</span>
                  </v-layout>
                </v-list-tile-title>
                <span
                  >Shortcut
                  <div class="shortcut-key">{{ item.shortcut }}</div></span
                >
              </v-tooltip>
              <v-list-tile-title
                v-else-if="
                  (item.name !== 'conversation' ||
                    !isFirmCommunicationDisabled) &&
                    (item.name !== 'documents' || !isFirmDocumentsDisabled)
                "
              >
                <v-layout align-center>
                  <i class="material-icons-outlined menu-icon mr-2">{{
                    item.icon
                  }}</i>
                  <span class="menu-text">{{
                    item.name ? $t('accountMenu.' + item.name) : item.title
                  }}</span>
                </v-layout>
              </v-list-tile-title>
            </v-list-tile>
            <v-divider
              v-if="$router.currentRoute.meta.newDashboard !== true"
              class="mx-2"
            />
            <v-list-tile @click="logout">
              <i class="material-icons-outlined menu-icon mr-2">login</i>
              <span class="menu-text">{{ $t('accountMenu.logOut') }}</span>
            </v-list-tile>
          </v-list>
        </v-menu>
      </sc-flex>
    </div>

    <!-- Slot: Aside -->
    <div slot="aside" v-if="hasNewTemplate">
      <NavigationClient
        v-if="isClientView"
        :current-company-id="currentCompanyId"
      />
      <NavigationLeft
        :unseen-conversations-count="unseenConversations.length"
        v-else
      />
    </div>

    <!-- Onboarding -->
    <div slot="footer-left">
      <sc-flex :gap="4" :is-column="true">
        <sc-flex v-if="isSorabanAdvisorAdmin">
          <sc-button-group>
            <sc-button
              icon-left="eye-closed"
              type="secondary"
              size="tiny"
              @click="toggleRedacted(currentUser)"
              >Redacted</sc-button
            >
          </sc-button-group>
        </sc-flex>
        <FirmOnboarding
          :onboarding-status="onboardingStatus"
          v-if="isFirmOnboardingEnabled"
        />
      </sc-flex>
    </div>

    <v-app id="embeddable" v-if="$route.meta.embeddable">
      <router-view />
    </v-app>

    <div id="app" v-else>
      <!-- TMP: Things below need the vuetify wrapper -->
      <v-app>
        <!-- Dialog: Send Mass Message -->
        <v-dialog
          v-model="sendMassMessageDialog"
          width="600"
          @keydown.esc="sendMassMessageDialog = false"
          persistent
        >
          <v-card block flat>
            <v-layout>
              <v-flex xs12>
                <v-card>
                  <v-card-title class="title pb-0">
                    Send Mass Email
                  </v-card-title>
                  <v-card-text>
                    <v-alert
                      :value="true"
                      color="info"
                      outline
                      class="mb-2 text-xs-left"
                    >
                      <span
                        >You can utilize variables such as
                        {{ userVariables }}</span
                      >
                    </v-alert>
                    <v-layout column>
                      <div class="text-xs-left darkgrey--text">Clients</div>
                      <v-autocomplete
                        multiple
                        class="default-input"
                        small-chips
                        solo
                        :items="emailList"
                        v-model="selectedUserIds"
                        placeholder="Select clients"
                        item-text="name_with_email"
                        item-value="id"
                      >
                        <template slot="selection" slot-scope="data">
                          <v-chip
                            small
                            v-if="selectedUserIds.indexOf(data.item.id) === 0"
                          >
                            <span>{{ data.item.name_with_email }}</span>
                          </v-chip>
                          <span
                            v-if="selectedUserIds.indexOf(data.item.id) === 1"
                            class="grey--text caption"
                            >(+{{ selectedUserIds.length - 1 }} other{{
                              selectedUserIds.length > 2 ? 's' : ''
                            }})</span
                          >
                        </template>
                      </v-autocomplete>
                      <div class="text-xs-left darkgrey--text">Subject</div>
                      <v-text-field
                        required
                        placeholder="Type a subject line for the email"
                        solo
                        :rules="textRule"
                        v-model="newSubject"
                        class="default-input"
                      />
                      <div class="text-xs-left darkgrey--text">Message</div>
                      <v-textarea
                        placeholder="Type the body text"
                        solo
                        v-model="newMessage"
                        required
                        :rules="textRule"
                        class="default-input"
                      />
                    </v-layout>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer />
                    <v-btn
                      class="default-input text-capitalize primary"
                      @click="sendMassMessage()"
                      :disabled="!massMessageSendable"
                    >
                      Send
                    </v-btn>
                    <v-btn
                      outline
                      class="default-input text-capitalize primary--text"
                      @click="sendTestMassMessage()"
                      :disabled="!massMessageSendable"
                    >
                      Send test email to myself
                    </v-btn>
                    <v-btn
                      outline
                      class="default-input text-capitalize error--text"
                      @click="sendMassMessageDialog = false"
                    >
                      Cancel
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-flex>
            </v-layout>
          </v-card>
        </v-dialog>

        <!-- NOTE @chris Will be removed -->
        <!-- Dialog: Conversation -->
        <ConversationDialog
          v-if="$route.path !== '/signin'"
          v-model="conversationDialogOpened"
          :conversation-id="selectedConversationId"
          @change-company="updateDefaultCompanyAndGoToDashboard"
        />

        <!-- NOTE @chris Will be removed -->
        <!-- Navigation: Sidebar -->
        <v-navigation-drawer
          app
          stateless
          touchless
          width="210"
          :value="true"
          class="navigation-drawer pt-4"
          style="z-index: 4"
          v-if="
            !hasNewTemplate &&
              $route.meta.requiresAuth &&
              $vuetify.breakpoint.mdAndUp &&
              !$route.meta.sideNavigationOff
          "
        >
          <the-navigation />
        </v-navigation-drawer>

        <!-- Dialog: Select Entity -->
        <SelectEntityDialog
          v-if="!isAdvisor"
          :opened.sync="selectEntityDialogOpened"
          @selectEntity="changeCompany"
        />

        <!-- Dialog: Locked My Cloud -->
        <LockedMyCloudDialog :opened.sync="lockedMyCloudDialogOpened" />

        <!-- Dialog: Select Conversation -->
        <v-dialog
          v-model="selectConversationDialogOpened"
          width="600"
          @keydown.esc="selectConversationDialogOpened = false"
        >
          <v-card block flat>
            <v-layout>
              <v-flex xs12>
                <v-card>
                  <v-card-title class="title"
                    >Select Conversation
                    <v-spacer />
                    <v-menu
                      transition="slide-y-transition"
                      bottom
                      nudge-bottom="40"
                      class="my-1"
                    >
                      <v-btn
                        slot="activator"
                        flat
                        class="white primary--text text-capitalize ma-0"
                      >
                        <v-icon class="mr-1"> mail_outline </v-icon>
                        Mass Email
                      </v-btn>
                      <v-list>
                        <v-list-tile @click="openSendMassMessageDialog('all')">
                          <v-list-tile-title> All Clients </v-list-tile-title>
                        </v-list-tile>
                        <v-list-tile
                          @click="openSendMassMessageDialog('assigned')"
                        >
                          <v-list-tile-title>
                            Assigned Clients
                          </v-list-tile-title>
                        </v-list-tile>
                        <v-list-tile @click="openSendMassMessageDialog('none')">
                          <v-list-tile-title>
                            Select Clients
                          </v-list-tile-title>
                        </v-list-tile>
                      </v-list>
                    </v-menu>
                    <v-btn
                      small
                      flat
                      fab
                      class="my-0 white error--text text-capitalize"
                      @click="selectConversationDialogOpened = false"
                    >
                      <v-icon size="24"> clear </v-icon>
                    </v-btn>
                  </v-card-title>
                  <v-card-text>
                    <v-layout column v-if="!isEmpty(allConversations)">
                      <div class="text-xs-left">Select Conversation</div>
                      <v-autocomplete
                        hide-details
                        placeholder="Select a conversation"
                        solo
                        v-model="selectedConversationId"
                        :items="allConversations"
                        item-value="id"
                        item-text="client_names"
                        class="default-input mb-4"
                        @change="
                          selectConversationDialogOpened = false;
                          conversationDialogOpened = true;
                        "
                      >
                        <template slot="item" slot-scope="data">
                          <span v-if="data.item.name"
                            >{{ data.item.name }} ({{
                              data.item.client_names.join(', ')
                            }})</span
                          >
                          <span v-else>{{
                            data.item.client_names.join(', ')
                          }}</span>
                        </template>
                      </v-autocomplete>
                    </v-layout>
                    <v-layout
                      column
                      align-center
                      v-else-if="conversationLoading"
                    >
                      <v-progress-circular
                        indeterminate
                        size="40"
                        color="primary"
                      ></v-progress-circular>
                    </v-layout>
                    <v-layout v-else-if="isEmpty(allConversations)">
                      <v-alert outline value="true" type="info">
                        There is no available conversations. Please add a client
                        first.
                      </v-alert>
                    </v-layout>
                    <v-layout align-center>
                      <v-spacer class="hidden-xs-only" />
                    </v-layout>
                  </v-card-text>
                </v-card>
              </v-flex>
            </v-layout>
          </v-card>
        </v-dialog>

        <!-- Partial The notification -->
        <the-notification />

        <!-- Partial: The Loading Overlay -->
        <the-loading-overlay :value="loading" />

        <!-- Partial: Router view -->
        <!-- The :key determines whether the page should re-render on route changes -->
        <!-- Same key means the page will not get re-rendered -->
        <router-view
          :key="
            $router.currentRoute.name === 'signin'
              ? 'signin'
              : $router.currentRoute.name === 'questionnaire'
              ? [$route.query.questionnaireId, $route.query.company_Id].join(
                  '-',
                )
              : $router.currentRoute.name === 'tax-return-delivery'
              ? ['tax-return-delivery', $route.params.id].join('-')
              : $route.params.hash
              ? $route.params.hash
              : $route.query.companyId
          "
          @openSpecificConversation="openSpecificConversation"
          @handleNotification="handleNotification"
        />

        <!-- NOTE @chris This is only necessary because we have to keep the v-app wrapper for vuetify. -->
        <div class="mt-3" v-if="!isEmbeddedInNext || !isInIframe"></div>

        <!-- Partial: Footer -->
        <sc-layout-footer
          v-if="
            this.$route.meta.requiresAuth &&
              !this.$route.meta.disableFooter &&
              !loading &&
              (!isEmbeddedInNext || !isInIframe)
          "
        >
          <a
            style="text-decoration: none"
            target="_blank"
            href="https://www.soraban.com/#terms"
            >Terms of Service</a
          >
          <a target="_blank" href="https://www.soraban.com/#policy"
            >Privacy Policy</a
          >
          <a rel="help" @click="goToHelpCenter()">Help</a>
        </sc-layout-footer>
      </v-app>
    </div>
  </sc-layout>
</template>

<script>
import API from '@/api';
import NavigationClient from '@/components/NavigationClient';
import NavigationLeft from '@/components/NavigationLeft';
import TheLoadingOverlay from '@/components/TheLoadingOverlay';
import TheNotification from '@/components/TheNotification';
import TheNavigation from '@/components/TheNavigation';
import ConversationDialog from '@/components/dialogs/ConversationDialog';
import RefreshUiAlert from '@/components/dialogs/RefreshUiAlert';
import LockedMyCloudDialog from '@/components/dialogs/LockedMyCloudDialog';
import SelectEntityDialog from '@/components/dialogs/SelectEntityDialog';
import SxP from '@/components/sx/SxP';
import SxTiny from '@/components/sx/SxTiny';
import SxFlex from '@/components/sx/SxFlex';
import SxIcon from '@/components/sx/SxIcon';
import SxButton from '@/components/sx/SxButton';
import SxButtonGroup from '@/components/sx/SxButtonGroup';
import { getSubdomain } from 'tldjs';
import { mapState, mapGetters } from 'vuex';
import { emailRules } from '@/constants/rules';
import { userVariables } from '@/constants/variables';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import endsWith from 'lodash/endsWith';
import moment from 'moment';
import firebase from 'firebase';
import GlobalUiAlert from './components/GlobalUiAlert';
import UtilsMixins from '@/mixins/UtilsMixins';
import FirmPreferenceMixins from './mixins/FirmPreferenceMixins';
import AdminClientsMixin from './mixins/AdminClientsMixin';
import FirmOnboarding from '@/components/FirmOnboarding';
import { DEFAULT_ONBOARDING_STATUS } from '@/constants/onboarding';
// import posthog from 'posthog-js';

document.addEventListener('click', _ =>
  window.parent.postMessage({ type: 'vue-app-iframe-click' }, '*'),
);

export default {
  name: 'App',
  components: {
    GlobalUiAlert,
    NavigationClient,
    NavigationLeft,
    TheLoadingOverlay,
    TheNotification,
    TheNavigation,
    ConversationDialog,
    RefreshUiAlert,
    LockedMyCloudDialog,
    SelectEntityDialog,
    SxButtonGroup,
    SxButton,
    SxFlex,
    SxP,
    SxTiny,
    SxIcon,
    FirmOnboarding,
  },
  mixins: [
    AdminClientsMixin,
    AdminClientsMixin,
    UtilsMixins,
    FirmPreferenceMixins,
  ],
  data() {
    return {
      exportBannerDismissed: undefined,
      userVariables,
      sendMassMessageDialog: false,
      newMessagesRef: null,
      listeningToNewMessages: null,
      clearTimerRef: null,
      listeningToNewNotifications: null,
      newNotificationsRef: null,
      listenersOn: false,
      globalAlertVisible: false,
      pendingRefresh: false,
      viewAllNotifications: false,
      viewAllConversations: true,
      lastRefreshed: moment().format('lll'),
      newMessage: '',
      conversationDialogOpened: false,
      selectedConversationId: null,
      selectConversationDialogOpened: false,
      selectedClient: null,
      conversationLoading: false,
      notificationLoading: true,
      conversationFullScreen: false,
      lockedMyCloudDialogOpened: false,
      allConversations: [],
      selectedUserIds: [],
      emailList: [],
      valid: false,
      email: '',
      password: '',
      emailRules,
      newSubject: '',
      passwordRules: [
        v => !!v || 'Password is required',
        v => v.length >= 6 || 'Password must be greater than 6 characters',
      ],
      textRule: [v => !!v || 'This field is required.'],
      currentCompanyId: null,
      selectEntityDialogOpened: false,
      onboardingStatus: DEFAULT_ONBOARDING_STATUS,
    };
  },
  computed: {
    ...mapState({
      currentUser: state => state.auth.user,
    }),
    massMessageSendable() {
      return (
        !isEmpty(this.newMessage) &&
        !isEmpty(this.newSubject) &&
        !isEmpty(this.selectedUserIds)
      );
    },
    ...mapState('companies', ['companies']),
    ...mapGetters({
      isFirmCommunicationDisabled: 'isFirmCommunicationDisabled',
      isFirmDocumentsDisabled: 'isFirmDocumentsDisabled',
      defaultCompany: 'defaultCompany',
      currentFirm: 'currentFirm',
      mobileNavigationItems: 'mobileNavigationItems',
      accountMenu: 'accountMenu',
      notifications: 'notifications',
      myConversations: 'myConversations',
      clientConversation: 'clientConversation',
    }),
    ...mapGetters(['isAuthenticated', 'isAdvisor', 'isSuperAdvisor']),
    ...mapState('application', ['loading', 'notification']),
    isClientView() {
      const isSelectedAdmin = this.$route.name === 'admin';
      const isSelectedDashboard = this.$route.name === 'admin-dashboard';
      const isSelectedDocuments = this.$route.name === 'admin-documents';
      const isSelectedConversation =
        this.$route.name === 'messages' && this.$route.path !== '/messages';
      const isSelectedReviewExport = this.$route.name === 'review-export';

      return Boolean(
        isSelectedAdmin ||
          isSelectedDashboard ||
          isSelectedDocuments ||
          isSelectedConversation ||
          isSelectedReviewExport,
      );
    },
    clientsMapped() {
      return [
        ...new Set(
          this.companies
            .map(company => company.user)
            .map(o => JSON.stringify(o)),
        ),
      ]
        .map(string => JSON.parse(string))
        .map(user => ({
          id: user.id,
          name_and_email: `${user.full_name} (${user.email})`,
          full_name: user.full_name,
          tax_return_name:
            this.companies.filter(c => c.user.id === user.id).length === 1 &&
            !this.isIndividual(this.companies.find(c => c.user.id === user.id))
              ? this.companies.find(c => c.user.id === user.id).name
              : user.tax_return_name,
          fname: user.fname,
          lname: user.lname,
          email: user.email,
          preferred_contact_method: user.preferred_contact_method,
          phone_number: user.phone_number,
          companies: this.companies.filter(c => c.user.id === user.id),
          assigned_advisor_id: user.assigned_advisor_id,
          new_messages: this.getMessages(user.id),
        }))
        .sort(
          (a, b) =>
            b.new_messages - a.new_messages ||
            a.tax_return_name.localeCompare(b.tax_return_name),
        );
    },
    slug() {
      return getSubdomain(window.location.href);
    },
    userInitials() {
      const parts = this.currentUser.full_name.split(' ').map(part => {
        return part && part.length ? part[0] : '';
      });

      return parts.join('');
    },
    companiesWithAdminView() {
      // return this.getCompaniesWithAdminView(this.companies)
      return this.companies.map(c => ({
        ...c,
        clientView: `${c.name} (${c.entity_type})`,
        adminView:
          c.entity_type === 'Individual'
            ? c.tax_return_name
            : `${c.name} (${c.user.full_name})`,
        superAdminView: `${c.name}`,
      }));
    },
    onlyIndividual() {
      return (
        this.companies.length === 1 &&
        this.companies[0].entity_type === 'Individual'
      );
    },
    clients() {
      return [
        ...new Set(
          this.companies
            .filter(company => company.user.id !== this.currentUser.id)
            .map(company => company.user)
            .map(o => JSON.stringify(o)),
        ),
      ]
        .map(string => JSON.parse(string))
        .sort((a, b) => a.full_name.localeCompare(b.full_name));
    },
    filteredNotifications() {
      if (this.viewAllNotifications) {
        return this.notifications.filter(
          n => moment(n.created_at) > moment().subtract(1, 'month'),
        );
      } else {
        return this.undismissedNotifications.slice(0, 7);
      }
    },
    undismissedNotifications() {
      return this.notifications.filter(
        n =>
          !n.dismissed && moment(n.created_at) > moment().subtract(1, 'month'),
      );
    },
    undismissedDownloadableNotifications() {
      return this.undismissedNotifications.filter(
        n => n && n.link && n.link.includes('!download'),
      );
    },
    downloadAllNotifications() {
      this.undismissedDownloadableNotifications.forEach(n => {
        this.downloadDocument(n);
        this.dismissNotification(n);
      });
    },
    filteredConversations() {
      if (this.viewAllConversations) {
        return this.myConversations;
      } else {
        return this.unseenConversations;
      }
    },
    isSorabanAdvisorAdmin() {
      return this.getIsSorabanAdvisorAdmin(this.currentUser || {});
    },
    colorOfConversationBadge() {
      if (
        this.myConversations.some(c =>
          c.unseen_messages.some(
            message =>
              message &&
              message.unseen_user_ids &&
              message.unseen_user_ids.includes(this.currentUser.id) &&
              message.tag === 'emergency',
          ),
        )
      ) {
        return 'error';
      } else if (
        this.myConversations.some(c =>
          c.unseen_messages.some(
            message =>
              message &&
              message.unseen_user_ids &&
              message.unseen_user_ids.includes(this.currentUser.id) &&
              message.tag === 'urgent',
          ),
        )
      ) {
        return 'warning';
      } else {
        return 'primary';
      }
    },
    unseenConversations() {
      return this.myConversations.filter(c =>
        c.unseen_messages.some(message =>
          message.unseen_user_ids.includes(this.currentUser.id),
        ),
      );
    },
    firmLogo() {
      if (this.currentFirm.medium_url) {
        return this.currentFirm.medium_url;
      }

      return null;
    },
    multiEntity() {
      return this.currentCompanyId && !this.onlyIndividual;
    },
  },
  methods: {
    moment,
    isEmpty,
    showSelectEntityDialog() {
      this.selectEntityDialogOpened = true;
    },
    async openSendMassMessageDialog(clientOption) {
      try {
        const response = await API.advisor.listAllUserEmails();

        this.emailList = response.data;
        this.selectedUserIds = [];

        if (clientOption === 'all') {
          this.selectedUserIds = this.emailList.map(e => e.id);
        }

        if (clientOption === 'assigned') {
          this.selectedUserIds = this.emailList
            .filter(e => e.assigned_advisor_id === this.currentUser.id)
            .map(e => e.id);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.sendMassMessageDialog = true;
        this.newMessage = '';
        this.newSubject = '';
      }
    },
    async sendMassMessage() {
      try {
        const response = await API.advisor.sendMassMessage({
          subject: this.newSubject,
          body: this.newMessage
            .trim()
            .split('\n')
            .join('<br/>'),
          clientIds: this.selectedUserIds,
        });
        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
          console.log(response.data.error);
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: "You've sent the message",
          });
          this.sendMassMessageDialog = false;
          this.newMessage = '';
          this.newSubject = '';
        }
      } catch (e) {
        console.log(e);
      } finally {
        this.sendMassMessageDialog = false;
      }
    },
    async sendTestMassMessage() {
      try {
        await API.advisor.sendTestMassMessage({
          subject: this.newSubject,
          body: this.newMessage
            .trim()
            .split('\n')
            .join('<br/>'),
        });

        this.$store.dispatch('application/setSuccessNotification', {
          message:
            "You've sent the test message. You should receive it within couple minutes.",
        });
      } catch (error) {
        console.error(error);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem sending the message.',
        });
      }
    },
    dismissTaxBanner(event) {
      event && event.stopPropagation();

      // posthog.capture('dismissed tax return delivery banner');

      return this.dismissBanner('has_banner_tax_return_delivery');
    },
    onTaxBannerClick() {
      // posthog.capture('opened link from tax return delivery banner');

      return (
        window &&
        window.open &&
        window.open(
          'https://soraban.notion.site/New-AI-Products-baa78892847a4df7abbc6239f089e6bd',
          '_blank',
        )
      );
    },
    async changeCompany(companyId) {
      const company = this.companies.find(c => c.id === companyId);

      if (company) {
        const hash =
          company.name
            .replaceAll(' ', '-')
            .replaceAll(',', '')
            .replaceAll('#', '') +
          '-' +
          company.hash_id;

        this.$router.push({
          name: this.$router.currentRoute.name,
          params: {
            hash,
          },
        });
      } else {
        this.$router.replace({
          name: this.$router.currentRoute.name,
          query: {
            companyId: companyId,
          },
        });
      }
      await this.$store.dispatch('setDefaultCompany', { companyId });

      this.selectEntityDialogOpened = false;
    },
    async markAsRead(conversation) {
      console.log('Mark as Read');
      console.log(JSON.stringify(this.filteredConversations));
      try {
        await API.conversation.updateConversationAsSeen(conversation.id);
        await this.$store.dispatch('updateConversationAsSeen', conversation.id);
      } catch (e) {
        console.log('Error setting conversation as seen');
        console.log(e);
      }
    },
    dismissAllNotifications() {
      this.undismissedNotifications.forEach(notification => {
        // eslint-disable-next-line
        notification.dismissed = true;
      });
      API.advisor.dismissAllNotifications();
    },
    isReminder(notification) {
      return notification.company_id === 0;
    },
    notificationColor(notification) {
      return notification.dismissed
        ? 'grey lighten-5'
        : 'lighten-5 bordered-item';
    },
    getNumberOfUnseenMessages(conversation) {
      return conversation.unseen_messages.filter(m =>
        m.unseen_user_ids.includes(this.currentUser.id),
      ).length;
    },
    getUnseenMessagesByCurrentUser(conversation) {
      const filteredList = conversation.unseen_messages.filter(m =>
        m.unseen_user_ids.includes(this.currentUser.id),
      );
      return filteredList[filteredList.length - 1];
    },
    getDocumentName(message) {
      return message.text.split('/')[message.text.split('/').length - 1];
    },
    async downloadDocument(notification) {
      const link = notification.link
        .replace('!download//Soraban', '')
        .replace('!download//soraban', '');
      const response = notification.company_id
        ? await API.integration.downloadDocument({
            companyId: notification.company_id,
            link,
          })
        : await API.user.downloadUserDocument({
            link,
          });
      if (response.data.document) {
        this.downloadDocumentByLink(
          response.data.document.data.link,
          response.data.document.data.name,
        );
      } else {
        console.log(response.data.error);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading the document.',
          errorToSend: response.data.error,
          userEmail: this.currentUser.email,
        });
      }
    },
    async downloadFolder(notification) {
      const link = notification.link
        .replace('!download_folder//Soraban', '')
        .replace('!download_folder//soraban', '');
      try {
        await API.integration.downloadFolder({
          link,
          companyId: notification.company_id,
          name: link.split('/')[link.split('/').length - 2],
        });
      } catch (err) {
        // Note from @jordan - A 503 means that the file size is too big.
        // I'm not sure if that's from AWS or Heroku or what. But that is
        // confirmed here: https://linear.app/soraban/issue/SOR-6869/[bug]-❓-unable-to-download-from-notification-dropdown
        const is503 = err.response && err.response.status === 503;

        if (is503) {
          this.$store.dispatch('application/setErrorNotification', {
            message: this.$t('documents.folderDownloadSizeTooBigErrorMessage'),
            timeout: 18000,
          });
        } else {
          this.$store.dispatch('application/setErrorNotification', {
            message: this.$t('documents.folderDownloadErrorMessage'),
          });
        }
      }
    },
    async goToNotificationLink(notification, openInNewWindow) {
      if (notification.company_id) {
        let path = '';

        if (
          notification.link.includes('companyId=' + notification.company_id)
        ) {
          path = notification.link;
        } else {
          if (notification.link.includes('?')) {
            path = notification.link + '&companyId=' + notification.company_id;
          } else {
            path = notification.link + '?companyId=' + notification.company_id;
          }
        }
        await this.$store.dispatch('resetTiles', {
          companyId: notification.company_id,
        });

        if (openInNewWindow === true) {
          window.open(path, '_blank');
        } else {
          this.$router.push(path);
        }
      } else {
        if (openInNewWindow === true) {
          window.open(notification.link, '_blank');
        } else {
          this.$router.push(notification.link);
        }
      }
    },
    async handleNotification(notification, openInNewWindow) {
      if (!notification.dismissed) {
        await API.advisor.dismissNotification(notification.id);
        this.refreshNotifications();
      }
      if (notification.link) {
        if (notification.link.includes('!download/')) {
          this.downloadDocument(notification);
        } else if (notification.link.includes('!download_folder/')) {
          this.downloadFolder(notification);
        } else if (notification.link.includes('!conversation/')) {
          this.openSpecificConversation(
            notification.link.replace('!conversation/', ''),
          );
        } else {
          this.goToNotificationLink(notification, openInNewWindow);
        }
      }
    },
    async downloadDocumentByLink(url, name) {
      const link = document.createElement('a');
      // If you don't know the name or want to use
      // the webserver default set name = ''
      link.setAttribute('download', name);
      link.target = '_blank';
      link.href = url;
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    dismissNotification(notification) {
      API.advisor.dismissNotification(notification.id);

      // eslint-disable-next-line
      notification.dismissed = true;
    },
    async refreshNotifications() {
      this.notificationLoading = true;
      const response = await API.advisor.getAllNotifications();
      this.$store.dispatch(
        'setAdvisorNotifications',
        response.data.sort((a, b) => (a.created_at > b.created_at ? -1 : 1)),
      );
      this.notificationLoading = false;
    },
    forWording(notification) {
      const company = this.companies.find(
        c => c.id === notification.company_id,
      );

      if (company) {
        if (
          company.entity_type !== 'Individual' ||
          company.user.tax_return_name !== notification.company_name
        ) {
          return ` for ${notification.company_name}`;
        } else {
          return '';
        }
      } else {
        return '';
      }
    },
    openSpecificConversation(id) {
      this.selectedConversationId = id;
      this.conversationDialogOpened = true;
    },
    async updateDefaultCompany(companyId) {
      const company = this.companies.find(c => companyId === c.id);

      this.$store.dispatch('setDefaultCompany', { companyId });
      this.$store.dispatch('resetTiles', {
        hashId: company.hash_id,
        clientName: company.name
          .replaceAll(' ', '-')
          .replaceAll(',', '')
          .replaceAll('#', ''),
      });

      this.$router.push({
        name: this.$router.currentRoute.name,
        params: {
          hash:
            company.name
              .replaceAll(' ', '-')
              .replaceAll(',', '')
              .replaceAll('#', '') +
            '-' +
            company.hash_id,
        },
      });
    },
    updateDefaultCompanyAndGoToDashboard(companyId) {
      const company = this.companies.find(c => companyId === c.id);
      this.$store.dispatch('setDefaultCompany', { companyId });
      this.$store.dispatch('resetTiles', {
        hashId: company.hash_id,
        clientName: company.name
          .replaceAll(' ', '-')
          .replaceAll(',', '')
          .replaceAll('#', ''),
      });
      if (this.currentUser.is_advisor) {
        this.$router.push({
          name: 'admin-dashboard',
          params: {
            hash:
              company.name
                .replaceAll(' ', '-')
                .replaceAll(',', '')
                .replaceAll('#', '') +
              '-' +
              company.hash_id,
          },
        });
      } else {
        this.$router.push({
          name: 'dashboard',
          params: {
            hash:
              company.name
                .replaceAll(' ', '-')
                .replaceAll(',', '')
                .replaceAll('#', '') +
              '-' +
              company.hash_id,
          },
        });
      }

      this.conversationDialogOpened = false;
    },
    goToPath(path) {
      if (this.$route.path !== path) {
        this.$router.push(path);
      }
    },
    get,
    endsWith,
    goToHelpCenter() {
      if (this.isAdvisor) {
        window.open('https://www.soraban.com/#hs-chat-open', '_blank');
      } else {
        this.$router.push('/help');
      }
    },
    logout() {
      this.conversationDialogOpened = false;
      this.$store.dispatch('userSignOut');
    },
    async refreshAll() {
      if (this.currentUser.id && this.$route.path !== '/signin') {
        this.$store.dispatch('getMyConversations');
      }
      if (this.currentUser.is_advisor && this.$route.path !== '/signin') {
        this.refreshNotifications();
      }
    },
    isIndividual(entity) {
      return entity.entity_type === 'Individual';
    },
    getMessages(user_id) {
      if (user_id === this.currentUser.id) {
        return 0;
      } else {
        return this.myConversations
          .filter(c => c.user_ids.includes(user_id))
          .reduce(
            (sum, conversation) =>
              sum +
              conversation.unseen_messages.filter(m =>
                m.unseen_user_ids.includes(this.currentUser.id),
              ).length,
            0,
          );
      }
    },
    async openConversationDialog(id) {
      this.selectedConversationId = id;
      this.conversationDialogOpened = true;
    },
    getNamesInConversationExceptMe(conversation) {
      if (this.isAdvisor) {
        if (!isEmpty(conversation.client_names)) {
          return conversation.client_names
            .filter(u => u !== this.currentUser.full_name)
            .join(', ');
        } else {
          return conversation.advisor_names
            .filter(u => u !== this.currentUser.full_name)
            .join(', ');
        }
      } else {
        return conversation.client_names
          .filter(u => u !== this.currentUser.full_name)
          .concat(this.currentFirm.name + ' Team')
          .join(', ');
      }
    },
    idleTracker(time) {
      let timeout;

      startTimer();

      document.addEventListener('mousemove', resetTimer);
      document.addEventListener('keydown', resetTimer);

      function startTimer() {
        timeout = setTimeout(logout, time);
      }

      function resetTimer() {
        clearTimeout(timeout);
        timeout = setTimeout(logout, time);
      }

      function clearTimer() {
        document.removeEventListener('mousemove', resetTimer);
        document.removeEventListener('keydown', resetTimer);
        clearTimeout(timeout);
      }

      function logout() {
        firebase.auth().signOut();
        clearTimer();
      }

      return clearTimer;
    },
    stopListeners() {
      this.listenersOn = false;
      if (this.clearTimerRef) {
        this.clearTimerRef();
        this.clearTimerRef = null;
      }

      if (this.listeningToNewNotifications) {
        this.newNotificationsRef.off('value', this.listeningToNewNotifications);
      }

      if (this.listeningToNewMessages) {
        this.newMessagesRef.off('value', this.listeningToNewMessages);
      }
    },
    startListeners() {
      this.listenersOn = true;
      this.startIdleTracking();
      this.startListeningNewMessages();
      this.startListeningNewNotifications();
    },
    onNewChatClick() {
      const hashUrl = this.getUserWithHashId(this.defaultCompany);
      const url = this.isAdvisor ? '/messages' : `/inbox/${hashUrl}`;

      return this.$router.push(url);
    },
    startIdleTracking() {
      const firmUserIdleTimeout = this.firmUserIdleTimeout
        ? this.firmUserIdleTimeout
        : 1000 * 60 * 60 * 24; // Default 24hours for firm users
      const clientIdleTimeout = this.clientIdleTimeout
        ? this.clientIdleTimeout
        : 1000 * 60 * 30; // Default 30minutes for clients

      const time = this.currentUser.is_advisor
        ? firmUserIdleTimeout
        : clientIdleTimeout;
      this.clearTimerRef = this.idleTracker(time);
    },
    startListeningNewMessages() {
      const database = firebase.database();

      this.newMessagesRef = database.ref(
        `users/${this.currentUser.id}/newMessages`,
      );
      this.listeningToNewMessages = this.newMessagesRef.on(
        'value',
        async () => {
          if (this.currentUser.id && this.$route.path !== '/signin') {
            await this.$store.dispatch('getMyConversations');
          }
        },
      );
    },

    startListeningNewNotifications() {
      const database = firebase.database();
      this.newNotificationsRef = database.ref(
        `users/${this.currentUser.id}/newNotifications`,
      );
      this.listeningToNewNotifications = this.newNotificationsRef.on(
        'value',
        async () => {
          if (
            this.currentUser.id &&
            this.currentUser.is_advisor &&
            this.$route.path !== '/signin'
          ) {
            await this.refreshNotifications();
          }
        },
      );
    },

    async setFirmFaviconBySlug() {
      const response = await API.firm.getFirmFaviconBySlug(
        this.slug,
        window.location.host,
      );

      if (response.data.url) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.getElementsByTagName('head')[0].appendChild(link);
        }
        link.href = response.data.url;
        document.title = response.data.name;
      }
    },
    onHomeClick() {
      const homeUrl = this.currentUser.is_advisor ? '/overview' : '/';

      return this.$router.push(homeUrl);
    },
  },
  watch: {
    $route: async function(_to, _from) {
      if (this.$route.query.companyId) {
        this.currentCompanyId = parseInt(this.$route.query.companyId);
      } else if (this.$route.params.hash) {
        const hash = this.$route.params.hash;
        const hashId = hash.substring(hash.lastIndexOf('-') + 1);
        try {
          const { data } = await API.company.getCompanyIdByHashId(hashId);

          this.currentCompanyId = data.id;
        } catch (error) {
          this.$router.push('/select-entity');
        }
      } else {
        this.currentCompanyId = this.defaultCompany.id;
      }

      this.refreshAll();

      // Load onboarding stattus if the flag is enabled and is advisor
      const isAuthenticatedRoute = this.$route.meta.requiresAuth;

      if (this.isFirmOnboardingEnabled && isAuthenticatedRoute) {
        const { data } = await API.firm.getFirmOnboardingStatus();
        this.onboardingStatus = data;
      }
    },
    selectConversationDialogOpened: {
      async handler(dialogOpened) {
        if (dialogOpened) {
          this.conversationLoading = true;
          const response = await API.advisor.getAllConversations();
          if (response.data.error) {
            console.log(response.data.error);
          } else {
            this.allConversations = response.data;
          }
          this.conversationLoading = false;
        }
      },
    },
    async currentUser(oldVal, newVal) {
      if (!this.listenersOn && newVal && newVal.id) {
        this.startListeners();
      } else if (this.listenersOn && !get(newVal, 'id', false)) {
        this.stopListeners();
      }

      this.addRedacted(newVal, this.currentFirm);
    },
  },
  async mounted() {
    this.setFirmFaviconBySlug();
    this.$watch(
      '$i18n.locale',
      (newLocale, oldLocale) => {
        if (newLocale === oldLocale) {
          return;
        }

        this.setDocumentLang(this.currentUser.language);
        this.setDocumentDirectionPerLocale(this.currentUser.language);
      },
      { immediate: true },
    );
  },
  // NOTE @chris We need to move this to vuex to improve the entity selector.
  async created() {
    if (this.$route.query.companyId) {
      this.currentCompanyId = parseInt(this.$route.query.companyId);
    } else if (this.$route.params.hash) {
      const hash = this.$route.params.hash;
      const hashId = hash.substring(hash.lastIndexOf('-') + 1);
      try {
        const { data } = await API.company.getCompanyIdByHashId(hashId);

        this.currentCompanyId = data.id;
      } catch (error) {
        this.$router.push('/select-entity');
      }
    } else {
      this.currentCompanyId = this.defaultCompany.id;
    }
  },
};
</script>

<style lang="css">
.navigation-left-logo {
  cursor: pointer;
}

.navigation-left-logo {
  cursor: pointer;
}

.logo {
  /* NOTE @chris Needs to match vuetify header size - 1px border. */
  max-height: 47px;
  max-width: 150px;
  object-fit: contain;
  border-style: none;
}
.mobile-logo {
  max-height: 30px;
  max-width: 90px;
  object-fit: contain;
  border-style: none;
}
.toolbar .v-toolbar__content {
  background-color: white !important;
  box-shadow: 0 0 5px 0 rgb(15 44 65 / 5%) !important;
  border: solid 1px #dfe3eb !important;
  z-index: 99;
}

.client-dashboard-wrapper .v-tabs__bar {
  z-index: 1;
  background-color: #f4f6fa !important;
  position: sticky;
  top: 167px;
  padding-bottom: 5px;
}

.client-dashboard-wrapper .message-container p {
  margin-bottom: 5px;
}

.client-dashboard-wrapper.single-entity .v-tabs__bar {
  top: 119px;
}

.client-dashboard-wrapper.mobile .v-tabs__bar {
  top: 158px;
}

.client-dashboard-wrapper.mobile.single-entity .v-tabs__bar {
  top: 108px;
}
</style>
<style scoped lang="postcss">
[v-cloak] {
  display: none;
}
#app {
  font-family: var(--font-family-sans);
  text-align: center;

  color: var(--gray-900);
}

#embeddable {
  font-family: var(--font-family-sans);
  background: #fff;
}
.feedback-button:before,
.feedback-button:hover:before {
  background-color: white;
  opacity: 1;
}
.feedback-button {
  text-transform: none;
}
.menu-icon {
  font-size: 14px;
  margin-right: 4px;
  color: #222222;
}
.menu-text {
  font-size: 14px;
  color: #222222;
}
.mobile-nav {
  background-color: white;
  height: 65px;
  border-top: 1px solid #dcdcdc;
}
.account__menu {
  color: #757575;
}
.notification__text {
  color: #616161;
  font-size: 14px;
}
.notification__time {
  color: #757575;
}
.messages-container {
  padding: 15px;
  border-radius: 8px;
  height: 400px;
  overflow-y: scroll;
}
.message-line {
  &__avatar {
    margin-right: 8px;
    margin-left: 8px;
  }
  &__timestamp {
    font-size: 12px;
    color: #a7a7a7;
  }
  &__text {
    width: 600px;
    border: 2px solid #d7d7d7;
    padding-top: 10px;
    padding-bottom: 10px;
    padding-right: 30px;
    padding-left: 30px;
    border-radius: 8px;
    text-align: left;
    &--user {
      border: 2px solid #e3ebff;
      background-color: #e3ebff;
    }
  }
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  margin-top: 10px;
  margin-bottom: 10px;
}
.message-input {
  margin-left: 15px;
  & >>> .v-input__slot {
    background-color: #f1eded !important;
    border-radius: 10px;
  }
}
.conversation {
  &__container {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    padding-right: 15px;
    padding-left: 15px;
    padding-top: 15px;
    padding-bottom: 15px;
    cursor: pointer;
  }
  &__date {
    display: flex;
    color: #a7a7a7;
    font-size: 11px;
  }
  &__message {
    display: flex;
    color: #373737;
    font-size: 14px;
    align-items: center;
  }
}

.scrollable {
  overflow-y: scroll;
}

.scrollable::-webkit-scrollbar:vertical {
  width: 8px;
}
.scrollable::-webkit-scrollbar-thumb {
  border-radius: 5px;
  border: 1px solid white; /* should match background, can't be transparent */
  background-color: rgba(0, 0, 0, 0.5);
}
.scrollable::-webkit-scrollbar {
  -webkit-appearance: none;
}
.bordered-item {
  border: #4f7eea solid 1px !important;
  background-color: rgba(79, 126, 234, 0.2);
  border-radius: 6px;
  margin: 10px;
}
.footer-text {
  color: #c1cbd5;
  a {
    color: #c1cbd5;
  }
}
.conversations-button,
.notifications-button {
  height: 35px;
  width: 35px;
}
.conversation-list-item {
  & >>> .v-list__tile {
    overflow: hidden;
    align-items: start;

    p {
      margin-bottom: 6px;
    }
  }
}

.current-entity__container {
  flex-grow: 0 !important;
}

.current-entity__container__switch--mobile {
  display: none;
  visibility: hidden;
}

@media (max-width: 640px) {
  .current-entity__container__switch--desktop {
    display: none;
  }

  .current-entity__container__switch--mobile {
    display: flex;
    visibility: visible;
  }
}

@media (max-width: 480px) {
  .current-entity__container {
    display: none;
  }
}

.default-company-name {
  word-break: break-all;
}

.sb-main-menu {
  will-change: bottom, top;
  will-change: opacity;
  will-change: transform;
  will-change: position;
}

/* NOTE @chris Workaround for the old client view layout */
@media (min-width: 960px) {
  .tmp-client-view-responsive-banner {
    padding-left: 200px;
  }
}

.changelog-image {
  background-color: var(--blue-100);
  border-radius: 6px;
}
</style>
